import React, { Fragment, useEffect, useMemo, useState } from "react";
import logo from "../../../assets/media/logo.png";
import GamingBoardBahamasLogo from "../../../assets/media/GamingBoardBahamasLogo.png";
import iconMark from "../../../assets/media/icon-mark.png";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import GBInput from "../../input";
import * as Yup from "yup";
import { signUp } from "../../../store/reducers/signUpSlice";
import {
  loginUser,
  registerUserData,
} from "../../../store/reducers/authSlice";
import { showError, showSuccess } from "../../../helpers/toast";

const parseSignupClaims = (jwt) => {
  if (!jwt || typeof jwt !== "string") return null;
  try {
    const payload = jwt.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

/**
 * Signup OTP step — enter SMS code, with Resend.
 * OTP is the invitation secretPin; Resend re-triggers delivery via signup-data.
 */
const SignUp3 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signUpUser = useSelector((state) => state.signUp);
  const loading = useSelector((state) => state.signUp.loading);
  const authLoading = useSelector((state) => state.auth.loading);
  const user = useSelector((state) => state.auth.user);
  const mfa = useSelector((state) => state.auth.mfa);
  const signupData = useSelector((state) => state?.signUp?.data?.payload);
  const [resendLoading, setResendLoading] = useState(false);

  const claims = useMemo(
    () => parseSignupClaims(signUpUser.signupcode),
    [signUpUser.signupcode]
  );
  const expectedOtp = claims?.secretPin != null ? String(claims.secretPin) : "";

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("OTP is required")
        .test("otp-match", "Enter correct OTP code", (value) =>
          expectedOtp ? String(value) === expectedOtp : false
        ),
    }),
    onSubmit: async () => {
      if (!signUpUser?.signupcode) {
        showError(
          "Signup session expired. Please use your invitation link again."
        );
        navigate("/signup-step1");
        return;
      }

      try {
        const signupResponse = await dispatch(
          signUp({
            userData: {
              signupcode: signUpUser.signupcode,
              password: signUpUser.password,
              full_name: signUpUser.full_name,
              phone: signUpUser.phone,
              companyRegistrationNo: signUpUser.companyRegistrationNo,
            },
          })
        ).unwrap();

        const userEmail =
          signupResponse?.user?.email ||
          signUpUser?.message?.user?.email ||
          user?.email;

        if (userEmail && signUpUser.password) {
          await dispatch(
            loginUser({
              emailOrPhone: userEmail?.toLowerCase()?.trim(),
              password: signUpUser.password,
            })
          );
        } else {
          navigate("/");
        }
      } catch (error) {
        const errorMessage =
          error?.message || error?.response?.data?.error || "";
        if (
          errorMessage.toLowerCase().includes("invalid signuo code") ||
          errorMessage.toLowerCase().includes("invalid signup code")
        ) {
          showError(
            "The link is expired. Please contact the service provider."
          );
        } else {
          showError(errorMessage || "Failed to complete signup");
        }
      }
    },
  });

  useEffect(() => {
    if (!signUpUser?.signupcode || !signUpUser?.password) {
      navigate("/signup-step1");
    }
  }, [navigate, signUpUser?.signupcode, signUpUser?.password]);

  useEffect(() => {
    if (user?.access_token || signupData?.access_token) {
      navigate("/dashboard");
    }
  }, [navigate, user, signupData]);

  // After signup auto-login, MFA may be required
  useEffect(() => {
    if (mfa?.token) {
      navigate("/login-mfa");
    }
  }, [mfa?.token, navigate]);

  const handleResendOtp = async () => {
    if (!signUpUser?.signupcode) {
      showError("Signup session expired. Please use your invitation link again.");
      navigate("/signup-step1");
      return;
    }
    setResendLoading(true);
    try {
      await dispatch(
        registerUserData({ signupcode: signUpUser.signupcode })
      ).unwrap();
      showSuccess("OTP resent to your phone number");
    } catch (error) {
      showError(
        error?.message ||
          error?.payload?.message ||
          "Failed to resend OTP. Please try again."
      );
    } finally {
      setResendLoading(false);
    }
  };

  const { values, handleChange, handleSubmit, errors, touched } = formik;
  const otpMatches = expectedOtp && values.otp === expectedOtp;
  const busy = loading || authLoading || resendLoading;

  return (
    <div className="App">
      <div className="container">
        <div className="landingWrapper">
          <div className="landingRow">
            <div className="landingCol">
              <div className="fieldsWrapper">
                <img src={logo} className="logo" alt="logo" />
                <h2>Signup With Us</h2>
                <p>We have sent an OTP to your given number</p>

                <form className="gb-login-form" onSubmit={handleSubmit}>
                  <Fragment>
                    <GBInput
                      name="otp"
                      type="text"
                      label="OTP"
                      value={values.otp}
                      placeholder="Enter OTP"
                      setValue={handleChange}
                      iconSrc={iconMark}
                    />
                    {(touched.otp || errors.otp) && errors.otp && (
                      <div className="error-message error-message-wrap">
                        {errors.otp}
                      </div>
                    )}
                  </Fragment>

                  <div className="gb-field-wrap">
                    <button
                      type="button"
                      className="forget-link border-0 bg-transparent p-0"
                      onClick={handleResendOtp}
                      disabled={busy}
                    >
                      {resendLoading ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value=""
                      id="flexCheckChecked"
                      defaultChecked
                      readOnly
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexCheckChecked"
                    >
                      I agree to <a href="#">Terms of Service</a> &{" "}
                      <a href="#">Privacy Policy</a>
                    </label>
                  </div>

                  <div className="gb-form-footer">
                    <button
                      type="submit"
                      style={{
                        background: !otpMatches ? "#ccc" : undefined,
                        border: !otpMatches ? "solid 1px #ccc" : undefined,
                      }}
                      disabled={!otpMatches || busy}
                      className="form-submit"
                    >
                      {loading ? "Signing Up..." : "Sign Up"}
                    </button>
                  </div>
                </form>

                <div className="gb-step-list">
                  <span className="current"></span>
                  <span className="current"></span>
                  <span className="current"></span>
                </div>
              </div>
            </div>
            <div className="landingCol">
              <div className="imageWrapper">
                <div className="loginBox">
                  <p>
                    Already have account? <Link to={"/"}>Sign In</Link>
                  </p>
                </div>
                <img
                  width={"300px"}
                  height={"300px"}
                  src={GamingBoardBahamasLogo}
                  className="booksAnimation"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp3;
